import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeature } from '@/features/feature-flags/useFeature';
import type { PatientViewModel } from '@/models/patient';

interface BreakGlassGateProps {
  readonly patient: PatientViewModel;
  readonly children: ReactNode;
}

/**
 * Frontend-only gate that blocks the patient-record content behind a
 * modal until the clinician acknowledges access to a sensitive record
 * (PTV2-1212 / S5 FE).
 *
 * Contract:
 *  - Not flagged, or `breakGlass.enabled` off → children render immediately.
 *  - Flagged + not-yet-acknowledged → modal renders alone, nothing else.
 *    Cancel returns to previous location (browser back if there's history,
 *    else `/`); Acknowledge POSTs `/break-glass/acknowledge` (audit trail)
 *    and reveals children.
 *  - Visit-scoped memory: acknowledgement lives in component state. Parent
 *    routes this component with `key={patient.guid}` so switching to
 *    another patient — or leaving and returning — remounts it and
 *    re-prompts. Reload also re-prompts. No sessionStorage / Zustand
 *    persistence, per BE story PTV2-1183.
 */
export function BreakGlassGate({
  patient,
  children,
}: BreakGlassGateProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breakGlassEnabled = useFeature('breakGlass.enabled') === true;
  const [acknowledged, setAcknowledged] = useState(false);

  const { sensitivity, guid } = patient;
  const shouldGate = breakGlassEnabled && sensitivity.flagged && !acknowledged;

  const acknowledgeMutation = useMutation<undefined, Error, undefined>({
    mutationFn: () => apiClient.acknowledgeBreakGlass(guid),
    onSuccess: () => {
      setAcknowledged(true);
    },
  });

  // Reset acknowledgement if the patient's sensitivity flag flips off
  // during the visit (e.g. a colleague or the current user clears it via
  // the Consents section). Keeping `acknowledged=true` in that case is
  // harmless, but resetting ensures the next flip-on re-prompts.
  useEffect(() => {
    if (!sensitivity.flagged) setAcknowledged(false);
  }, [sensitivity.flagged]);

  const handleCancel = useCallback((): void => {
    // history.length > 1 means the user got here from another page.
    // Fall back to the worklist so a fresh-tab open still has an exit.
    // Cast to void because react-router-dom v7's navigate() returns a
    // promise; we don't need to await the transition here.
    if (window.history.length > 1) {
      void navigate(-1);
    } else {
      void navigate('/', { replace: true });
    }
  }, [navigate]);

  if (!shouldGate) {
    return <>{children}</>;
  }

  return (
    <Dialog
      open
      // Radix normally closes on Esc / overlay click. This is a gate, so
      // any close attempt routes through the same "Cancel" flow — user
      // never lands on the PHI without an acknowledgement.
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleCancel();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            {t('patient.breakGlass.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {sensitivity.reason !== null && sensitivity.reason.length > 0 && (
            <p className="rounded-md bg-muted/60 px-3 py-2 text-foreground/90">
              <span className="text-muted-foreground">
                {t('patient.breakGlass.reasonLabel')}
                {': '}
              </span>
              {sensitivity.reason}
            </p>
          )}
          <p className="text-muted-foreground">
            {t('patient.breakGlass.auditNotice')}
          </p>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={acknowledgeMutation.isPending}
          >
            {t('patient.breakGlass.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              acknowledgeMutation.mutate(undefined);
            }}
            loading={acknowledgeMutation.isPending}
          >
            {t('patient.breakGlass.acknowledge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
